/**
 * TemplatesView — `/templates`
 *
 * Catálogo del tenant, full-page (B.6.d.1). Alineada al prototipo:
 *   - Header con eyebrow "Catálogo del tenant" + título + counts
 *     (system-default + custom) + botón "Nueva plantilla custom".
 *   - Tabs Todas / System default / Custom del tenant (con badges de
 *     count cada uno) + Metodologías (OT-0055: la lista de metodologías
 *     en vez de plantillas).
 *   - Filtro por tipo (Todos · Ciclo · Artefacto · ...) y por metodología
 *     (Todas · Genéricas · cada metodología). Cada card dice a qué
 *     metodología pertenece la plantilla, o "Base" / "Genérica".
 *   - Search input (filtra ILIKE name + template_key del lado backend).
 *   - Grid 3-col responsive de cards.
 *   - Click en card → `/templates/{id}` (detail / Designer en B.6.d.2).
 *
 * El detail view sigue siendo página separada (B.5.b). Cuando entre el
 * Designer real (B.6.d.2), lo reemplazará.
 */

import { useCallback, useMemo, useState } from 'react'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Plus, Search } from 'lucide-react'

import { ApiError, isForbiddenTier } from '../api/client'
import { retryUnlessForbiddenTier } from '../api/queryRetry'
import {
  listTemplates,
  type Template,
  type TemplateType,
} from '../api/templates'
import { useAccessToken } from '../auth/useAccessToken'
import { useMe } from '../auth/useMe'
import { canManageTenant } from '../lib/tiers'
import { templateMethodologyLabel } from '../lib/methodologyLabel'
import { NewTemplateDialog } from '../components/templates/NewTemplateDialog'
import { MethodologiesPanel } from '../components/templates/methodologies/MethodologiesPanel'
import { ScrollPage } from '../components/shell/ScrollPage'
import { NoAccessView } from './NoAccessView'
import { useUrlSearchInput } from '../hooks/useUrlSearchInput'
import { useMethodologies } from '../hooks/useMethodologies'
import {
  GENERIC_METHODOLOGY_FILTER,
  type TemplatesTabParam,
  type TemplatesTypeParam,
} from '../router/router'

type Tab = TemplatesTabParam
type SortKey =
  | 'name_asc'
  | 'name_desc'
  | 'updated_desc'
  | 'created_desc'
  | 'created_asc'

// Filtros del catálogo: 4 tipos top-level. `phase` y `subphase` NO
// aparecen — viven embebidos dentro del lifecycle template, no son
// templates standalone — ni `methodology` (el "modo de ejecución"
// reservado, Doc 06). El backend mantiene esos valores en TemplateType
// para forward-compat, pero el frontend no los expone.
// El tipo se acota a `TemplatesTypeParam` (subset validado por el
// router) para que el URL search param mantenga el mismo dominio.
const TYPES: TemplatesTypeParam[] = [
  'lifecycle',
  'artifact',
  'approval_workflow',
  'role',
]

export function TemplatesView() {
  const { t, i18n } = useTranslation(['templates', 'errors', 'common'])
  const { me } = useMe()
  const { accessToken, loading: authLoading } = useAccessToken()
  // OT-0009 §1.2 #12 — "Nueva plantilla" es admin únicamente
  // (`require_tenant_admin` en `create_template`); antes se ofrecía a
  // cualquiera y el backend rechazaba al enviar.
  // Crear plantillas y gestionar metodologías: admin con la organización
  // permitiendo escribir (OT-0073 §4.4).
  const canManage = canManageTenant(me?.role, me?.tenant?.access)

  // Filtros viven en URL search params (`?tab=system&type=artifact&q=…&sort=…`).
  // Eso permite que "Volver al catálogo" desde el detalle preserve el
  // contexto, y que los PMs compartan links a vistas filtradas. El
  // router valida los valores; aplicamos defaults cuando no hay param.
  const navigate = useNavigate({ from: '/templates' })
  const searchParams = useSearch({ from: '/templates' })
  const tab: Tab = searchParams.tab ?? 'all'
  const typeFilter: TemplatesTypeParam | '' = searchParams.type ?? ''
  const sortBy: SortKey = searchParams.sort ?? 'name_asc'
  const methodologyFilter = searchParams.methodology ?? ''
  const { methodologies, byId: methodologiesById } = useMethodologies()

  // El input de búsqueda se confirma a la URL tras 300ms sin teclear, para
  // no inflar el historial ni re-fetch por cada tecla. Si vacío, omite el
  // param para mantener URLs limpias.
  const commitSearch = useCallback(
    (q: string) =>
      void navigate({
        search: (prev) => {
          const next = { ...prev }
          if (q.trim() === '') delete next.q
          else next.q = q
          return next
        },
        replace: true,
      }),
    [navigate],
  )
  const [searchInput, setSearchInput] = useUrlSearchInput(
    searchParams.q ?? '',
    commitSearch,
  )

  // Search consumido por el query (alimentado por el value de URL para
  // que un share/bookmark inicie con el filtro aplicado sin esperar al
  // debounce del input).
  const search = searchParams.q ?? ''

  const setTab = (next: Tab) =>
    void navigate({
      search: (prev) => {
        const out = { ...prev }
        if (next === 'all') delete out.tab
        else out.tab = next
        return out
      },
      replace: true,
    })
  const setTypeFilter = (next: TemplatesTypeParam | '') =>
    void navigate({
      search: (prev) => {
        const out = { ...prev }
        if (next === '') delete out.type
        else out.type = next
        return out
      },
      replace: true,
    })
  const setMethodologyFilter = (next: string) =>
    void navigate({
      search: (prev) => {
        const out = { ...prev }
        if (next === '') delete out.methodology
        else out.methodology = next
        return out
      },
      replace: true,
    })
  const setSortBy = (next: SortKey) =>
    void navigate({
      search: (prev) => {
        const out = { ...prev }
        if (next === 'name_asc') delete out.sort
        else out.sort = next
        return out
      },
      replace: true,
    })

  const [createOpen, setCreateOpen] = useState(false)

  // Cargamos todas las visibles una sola vez por (typeFilter, search) — el
  // tab Custom/System filtra cliente-side (es-cheap, evita doble round-trip).
  //
  // `i18n.language` va en el queryKey porque el backend resuelve
  // `name`/`description` desde `definition.i18n.{locale}` según el
  // `Accept-Language` (Doc 19 §"Resolución de metadatos de plantillas").
  // Sin esta dimensión el cache cross-locale serviría el idioma anterior
  // al volver a montar la vista tras un cambio de idioma.
  const query = useQuery({
    queryKey: [
      'templates',
      'catalog',
      i18n.language,
      typeFilter || 'all',
      search.trim(),
    ],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return listTemplates(accessToken, {
        ...(typeFilter ? { template_type: typeFilter } : {}),
        ...(search.trim() ? { search: search.trim() } : {}),
      })
    },
    enabled: !!accessToken,
    retry: retryUnlessForbiddenTier,
  })

  const all = useMemo(() => query.data ?? [], [query.data])
  const systemCount = useMemo(
    () => all.filter((t) => t.is_system_default).length,
    [all],
  )
  const customCount = useMemo(
    () => all.filter((t) => !t.is_system_default).length,
    [all],
  )
  const total = all.length

  const filteredByTab = useMemo(() => {
    const byTab =
      tab === 'system'
        ? all.filter((t) => t.is_system_default)
        : tab === 'custom'
          ? all.filter((t) => !t.is_system_default)
          : all
    if (methodologyFilter === '') return byTab
    const wanted =
      methodologyFilter === GENERIC_METHODOLOGY_FILTER ? null : methodologyFilter
    return byTab.filter((t) => t.methodology_id === wanted)
  }, [all, tab, methodologyFilter])

  const sortedTemplates = useMemo(() => {
    const list = [...filteredByTab]
    const lang = i18n.language || 'es'
    list.sort((a, b) => {
      switch (sortBy) {
        case 'name_asc':
          return a.name.localeCompare(b.name, lang)
        case 'name_desc':
          return b.name.localeCompare(a.name, lang)
        case 'updated_desc':
          return b.updated_at.localeCompare(a.updated_at)
        case 'created_desc':
          return b.created_at.localeCompare(a.created_at)
        case 'created_asc':
          return a.created_at.localeCompare(b.created_at)
        default:
          return 0
      }
    })
    return list
  }, [filteredByTab, sortBy, i18n.language])

  // La denegación del backend manda sobre cualquier dato previo en caché:
  // React Query conserva `data` cuando un refetch falla.
  if (isForbiddenTier(query.error)) return <NoAccessView />

  const tenantName = me?.tenant?.display_name ?? '—'

  return (
    <ScrollPage>
      <div className="max-w-7xl">
      {/* Header */}
      <header className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-wider font-semibold text-ink-subtle mb-1">
            {t('catalog.eyebrow')}
          </p>
          <h1 className="font-display text-3xl text-ink">
            {t('page_title')}
          </h1>
          <p className="mt-1 font-body text-sm text-ink-muted">
            {total === 0
              ? t('catalog.subtitle_zero')
              : t('catalog.subtitle', {
                  system: systemCount,
                  custom: customCount,
                  tenantName,
                })}
          </p>
        </div>
        {canManage ? (
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 font-ui text-sm text-canvas hover:bg-brand-deep transition-colors"
          >
            <Plus className="w-4 h-4" />
            {t('catalog.new_button')}
          </button>
        ) : null}
      </header>

      {/* Tabs */}
      <nav
        role="tablist"
        className="flex items-center gap-0 border-b border-line mb-5"
      >
        <TabButton
          active={tab === 'all'}
          label={t('catalog.tabs.all')}
          count={total}
          onClick={() => setTab('all')}
        />
        <TabButton
          active={tab === 'system'}
          label={t('catalog.tabs.system')}
          count={systemCount}
          onClick={() => setTab('system')}
        />
        <TabButton
          active={tab === 'custom'}
          label={t('catalog.tabs.custom')}
          count={customCount}
          onClick={() => setTab('custom')}
        />
        <TabButton
          active={tab === 'methodologies'}
          label={t('catalog.tabs.methodologies')}
          count={methodologies.length}
          onClick={() => setTab('methodologies')}
        />
      </nav>

      {tab === 'methodologies' ? (
        <MethodologiesPanel canManage={canManage} />
      ) : (
      <>
      {/* Filtros */}
      <div className="bg-surface border border-line rounded-md p-3 mb-5 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-260px max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-subtle" />
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t('catalog.search_placeholder')}
            className="w-full bg-canvas border border-line rounded-md pl-9 pr-3 py-1.5 text-sm focus:outline-none focus:border-brand"
          />
        </div>
        <span className="text-xs text-ink-muted">
          {t('catalog.type_filter_label')}
        </span>
        <button
          type="button"
          onClick={() => setTypeFilter('')}
          className={
            typeFilter === ''
              ? 'rounded-md bg-brand-soft text-brand text-xs px-2.5 py-1 font-medium'
              : 'rounded-md text-xs px-2.5 py-1 text-ink-muted hover:text-ink'
          }
        >
          {t('list.filters.all')}
        </button>
        {TYPES.map((tp) => (
          <button
            key={tp}
            type="button"
            onClick={() => setTypeFilter(tp)}
            className={
              typeFilter === tp
                ? 'rounded-md bg-brand-soft text-brand text-xs px-2.5 py-1 font-medium'
                : 'rounded-md text-xs px-2.5 py-1 text-ink-muted hover:text-ink'
            }
          >
            {t(`types.${tp}` as 'types.artifact')}
          </button>
        ))}

        <label className="flex items-center gap-2 text-xs text-ink-muted">
          {t('catalog.methodology_filter_label')}
          <select
            value={methodologyFilter}
            onChange={(e) => setMethodologyFilter(e.target.value)}
            className="bg-canvas border border-line rounded-md px-2 py-1 text-xs text-ink focus:outline-none focus:border-brand"
          >
            <option value="">{t('catalog.methodology_filter.all')}</option>
            <option value={GENERIC_METHODOLOGY_FILTER}>
              {t('catalog.methodology_filter.generic')}
            </option>
            {methodologies.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </label>

        {/* Separator + sort selector */}
        <span className="ml-auto text-xs text-ink-muted">
          {t('catalog.sort_label')}
        </span>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as SortKey)}
          aria-label={t('catalog.sort_label')}
          className="bg-canvas border border-line rounded-md px-2 py-1 text-xs focus:outline-none focus:border-brand"
        >
          <option value="name_asc">{t('catalog.sort.name_asc')}</option>
          <option value="name_desc">{t('catalog.sort.name_desc')}</option>
          <option value="updated_desc">{t('catalog.sort.updated_desc')}</option>
          <option value="created_desc">{t('catalog.sort.created_desc')}</option>
          <option value="created_asc">{t('catalog.sort.created_asc')}</option>
        </select>
      </div>

      {/* Body */}
      {authLoading || query.isLoading ? (
        <p className="font-mono text-xs text-ink-subtle">
          {t('catalog.loading')}
        </p>
      ) : query.error ? (
        <ErrorBox error={query.error} />
      ) : total === 0 ? (
        <EmptyState mode="global" />
      ) : filteredByTab.length === 0 ? (
        <EmptyState mode="filtered" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sortedTemplates.map((tpl) => (
            <TemplateCard
              key={tpl.id}
              template={tpl}
              methodologyLabel={templateMethodologyLabel(
                tpl,
                methodologiesById,
                t,
              )}
            />
          ))}
        </div>
      )}
      </>
      )}

      <NewTemplateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
      </div>
    </ScrollPage>
  )
}

// ─── Tab button ────────────────────────────────────────────────────────────
function TabButton({
  active,
  label,
  count,
  onClick,
}: {
  active: boolean
  label: string
  count: number
  onClick: () => void
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={
        active
          ? 'px-4 py-2.5 text-sm font-medium border-b-2 border-brand text-brand flex items-center gap-2'
          : 'px-4 py-2.5 text-sm font-medium border-b-2 border-transparent text-ink-muted hover:text-ink transition-colors flex items-center gap-2'
      }
    >
      {label}
      <span
        className={
          active
            ? 'text-[11px] font-mono text-brand/70'
            : 'text-[11px] font-mono text-ink-subtle'
        }
      >
        {count}
      </span>
    </button>
  )
}

// ─── Card ──────────────────────────────────────────────────────────────────
function TemplateCard({
  template,
  methodologyLabel,
}: {
  template: Template
  methodologyLabel: string
}) {
  const { t } = useTranslation('templates')

  // Type badge color por tipo, alineado a Doc 18 + prototipo:
  //   artifact      → brand-soft
  //   phase / subphase → official-soft
  //   methodology   → review-soft
  //   approval      → high-soft (warning visual)
  //   role          → ink-muted (neutral)
  const typeBadgeClass = TYPE_BADGE_CLASS[template.template_type]

  const ownerLabel = template.is_system_default
    ? t('catalog.card.owner_system')
    : template.status === 'draft'
      ? t('catalog.card.owner_draft')
      : t('catalog.card.owner_custom')

  // Versión real del current_version_id (ej. "v3.4.0"). El backend la
  // resuelve con JOIN a `template_versions` y la expone como
  // `current_version`. Fallback a guion cuando no hay versión activa.
  const versionLine = template.current_version
    ? `v${template.current_version}`
    : 'v—'

  return (
    <Link
      to="/templates/$templateId"
      params={{ templateId: template.id }}
      className="group bg-surface border border-line rounded-md p-4 hover:border-line-strong transition-colors flex flex-col"
    >
      {/* Top row: type badge · meta */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <span
          className={`px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider ${typeBadgeClass}`}
        >
          {t(`types.${template.template_type}` as 'types.artifact')}
        </span>
        <span className="font-mono text-[10px] text-ink-subtle">
          {versionLine} · {ownerLabel}
        </span>
      </div>

      {/* Name */}
      <h3 className="font-display text-base font-semibold text-ink leading-snug">
        {template.name}
      </h3>

      {/* Description */}
      {template.description ? (
        <p className="mt-1 font-body text-xs text-ink-muted line-clamp-2 leading-snug">
          {template.description}
        </p>
      ) : null}

      {/* Footer: template_key · metodología */}
      <div className="mt-auto pt-3 border-t border-line/60 flex items-center gap-2">
        <span className="text-[10px] font-mono text-ink-subtle truncate mr-auto">
          {template.template_key}
        </span>
        {methodologyLabel ? (
          <span
            title={methodologyLabel}
            className="max-w-[45%] truncate px-1.5 py-0.5 rounded text-[9px] font-medium uppercase tracking-wider bg-surface-muted text-ink-muted"
          >
            {methodologyLabel}
          </span>
        ) : null}
        {/* Status chip si NO es published (info adicional) */}
        {template.status !== 'published' ? (
          <StatusChip status={template.status} />
        ) : null}
      </div>
    </Link>
  )
}

const TYPE_BADGE_CLASS: Record<TemplateType, string> = {
  artifact: 'bg-brand-soft text-brand',
  lifecycle: 'bg-official-soft text-official',
  phase: 'bg-official-soft text-official',
  subphase: 'bg-official-soft text-official',
  methodology: 'bg-review-soft text-review',
  approval_workflow: 'bg-review-soft text-review',
  role: 'bg-surface-muted text-ink-muted',
}

function StatusChip({ status }: { status: Template['status'] }) {
  const { t } = useTranslation('templates')
  const className =
    status === 'draft'
      ? 'bg-surface-muted text-ink-subtle'
      : status === 'deprecated'
        ? 'bg-review-soft text-review'
        : 'bg-surface-muted text-ink-subtle line-through'
  return (
    <span
      className={`px-1.5 py-0.5 rounded text-[9px] font-medium uppercase tracking-wider ${className}`}
    >
      {t(`status.${status}` as 'status.draft')}
    </span>
  )
}

// ─── Empty + error ─────────────────────────────────────────────────────────
function EmptyState({ mode }: { mode: 'global' | 'filtered' }) {
  const { t } = useTranslation('templates')
  const key =
    mode === 'global' ? 'catalog.empty_global' : 'catalog.empty_filtered'
  return (
    <div className="rounded-md border border-dashed border-line bg-surface p-10 text-center">
      <p className="font-body text-sm text-ink-muted">
        {t(key as 'catalog.empty_global')}
      </p>
    </div>
  )
}

function ErrorBox({ error }: { error: unknown }) {
  const { t } = useTranslation(['templates', 'errors'])
  const code = error instanceof ApiError ? error.code : 'UNKNOWN'
  const fallback =
    error instanceof ApiError
      ? error.body?.message ?? error.message
      : error instanceof Error
        ? error.message
        : 'unknown'
  return (
    <div className="rounded-md border border-critical/40 bg-critical-soft p-4 font-mono text-xs text-critical">
      {t(`errors:${code}` as 'errors:UNKNOWN', { defaultValue: fallback })}
    </div>
  )
}
